import { EventType } from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'
import { Pages } from './Pages'

import { Center, Container, Loader } from '@mantine/core'
import { Notifications, notifications } from '@mantine/notifications'
import { APIProvider } from '@vis.gl/react-google-maps'
import { useCallback, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import classes from './App.module.css'
import { b2cPolicies, protectedResources } from './authConfig'
import ErrorBoundary from './components/ErrorBoundary'
import { Footer } from './components/Footer'
import { TopMenu } from './components/TopMenu'
import { SearchProvider } from './contexts/CityFilterContext'
import { useErrorContext } from './contexts/ErrorContext'
import { NotificationProvider } from './contexts/NotificationContext'
import { PendingApprovalProvider } from './contexts/PendingApprovalContext'
import { useSessionInfo } from './contexts/SessionContext'
import { fetchAdvertisementsInCart, fetchAllLocationsBundlesInCart } from './data/checkout.service'
import {
  fetchMyOrganization,
  fetchOnboardingState,
} from './data/organization.service'
import { fetchMyUser } from './data/user.service'
import { fetchCategories } from './data/location.service'
import {
  compareIssuingPolicy,
  isAccountInfo,
  isAuthenticationResult,
} from './utils/authUtils'

function App() {
  const location = useLocation()
  const { error } = useErrorContext()
  /**
   * useMsal is hook that returns the PublicClientApplication instance,
   * an array of all accounts currently signed in and an inProgress value
   * that tells you what msal is currently doing. For more, visit:
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/hooks.md
   */
  const { inProgress, instance } = useMsal()
  const userIsLoggingIn =
    inProgress === 'startup' ||
    inProgress === 'handleRedirect' ||
    inProgress === 'login'
  const { session, setSession } = useSessionInfo()
  const account = instance.getActiveAccount()
  const shouldShowHeaderAndFooter =
    location.pathname.toLowerCase() !== '/hostonboarding' &&
    (session == null || session?.isAdvertiserOnboardingComplete)

  const getSessionInfo = useCallback(
    async (userId: string) => {
      if (
        userId != null &&
        session == null &&
        instance != null &&
        inProgress === 'none' // Only proceed when MSAL is fully initialized
      ) {
        try {
          const user = await fetchMyUser(instance)
          const organization = await fetchMyOrganization(instance)
          const [itemsInCart, allLocationsBundles] = await Promise.all([
            fetchAdvertisementsInCart(organization.id, instance),
            fetchAllLocationsBundlesInCart(organization.id, instance),
          ])

          let onboardingState = null
          try {
            onboardingState = await fetchOnboardingState(
              organization.id,
              instance,
            )
          } catch (error: any) {
            if (error?.response?.status !== 404) {
              throw error
            }
          }

          // Fetch categories from database
          const categories = await fetchCategories()

          setSession({
            user: user,
            organizationId: organization.id,
            isAdvertiserOnboardingComplete:
              organization.categoryIds?.length > 0 && !!organization.name,
            isHostOnboardingComplete: onboardingState?.isComplete || false,
            isHostOnboardingStarted: onboardingState != null,
            itemsInCart,
            allLocationsBundleCount: allLocationsBundles.length,
            organizationCategories:
              organization.categoryIds?.map(
                (id) =>
                  categories.find((c) => c.id === id) || {
                    id,
                    name: 'Unknown Category',
                  },
              ) || [],
          })
        } catch (ex) {
          notifications.show({
            title: 'Error',
            message:
              'Unable to load your session information. Please refresh the page and try again.',
            color: 'red',
            autoClose: false,
          })
          console.error('Error getting session:', ex)
        }
      }
    },
    [setSession, session, instance, inProgress],
  )

  useEffect(() => {
    if (account != null && session == null) {
      getSessionInfo(account.localAccountId)
    }
  }, [getSessionInfo, account, location, session])

  useEffect(() => {
    const callbackId = instance.addEventCallback(async (event) => {
      if (
        (event.eventType === EventType.LOGIN_SUCCESS ||
          event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
        event?.payload &&
        isAuthenticationResult(event.payload)
      ) {
        getSessionInfo(event.payload.account.localAccountId)

        /**
         * For the purpose of setting an active account for UI update, we want to consider only the auth
         * response resulting from SUSI flow. "tfp" claim in the id token tells us the policy (NOTE: legacy
         * policies may use "acr" instead of "tfp"). To learn more about B2C tokens, visit:
         * https://docs.microsoft.com/en-us/azure/active-directory-b2c/tokens-overview
         */
        if (
          compareIssuingPolicy(
            event.payload.idTokenClaims,
            b2cPolicies.names.editProfile,
          )
        ) {
          // retrieve the account from initial sing-in to the app
          if (event?.payload) {
            const originalSignInAccount = instance
              .getAllAccounts()
              .find(
                (account) =>
                  isAccountInfo(event?.payload) &&
                  account.idTokenClaims?.oid ===
                    event.payload.idTokenClaims?.oid &&
                  account.idTokenClaims?.sub ===
                    event.payload.idTokenClaims?.sub &&
                  compareIssuingPolicy(
                    account.idTokenClaims,
                    b2cPolicies.names.signUpSignIn,
                  ),
              )

            let signUpSignInFlowRequest = {
              authority: b2cPolicies.authorities.signUpSignIn.authority,
              account: originalSignInAccount,
            }

            // silently login again with the signUpSignIn policy
            instance.ssoSilent(signUpSignInFlowRequest)
          }
        }

        /**
         * Below we are checking if the user is returning from the reset password flow.
         * If so, we will ask the user to reauthenticate with their new password.
         * If you do not want this behavior and prefer your users to stay signed in instead,
         * you can replace the code below with the same pattern used for handling the return from
         * profile edit flow
         */
        if (
          compareIssuingPolicy(
            event.payload.idTokenClaims,
            b2cPolicies.names.forgotPassword,
          )
        ) {
          let signUpSignInFlowRequest = {
            authority: b2cPolicies.authorities.signUpSignIn.authority,
            scopes: [...protectedResources.scopes.read],
          }
          instance.loginRedirect(signUpSignInFlowRequest)
        }
      }

      if (event.eventType === EventType.LOGIN_FAILURE) {
        console.error('Login failure:', event.error)

        // Check for forgot password error
        // Learn more about AAD error codes at https://docs.microsoft.com/en-us/azure/active-directory/develop/reference-aadsts-error-codes
        if (event.error && event.error.message.includes('AADB2C90118')) {
          const resetPasswordRequest = {
            authority: b2cPolicies.authorities.forgotPassword.authority,
            scopes: [],
          }
          instance.loginRedirect(resetPasswordRequest)
        }

        // Handle session errors (AADB2C90077: User does not have an existing session)
        if (event.error && event.error.message.includes('AADB2C90077')) {
          console.log('Session error detected, attempting to re-authenticate')
          const loginRequest = {
            authority: b2cPolicies.authorities.signUpSignIn.authority,
            scopes: [...protectedResources.scopes.read],
          }
          instance.loginRedirect(loginRequest)
        }
      }
    })

    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId)
      }
    }
    // eslint-disable-next-line
  }, [instance, setSession])

  return (
    <ErrorBoundary>
      <Notifications />
      <NotificationProvider>
        <PendingApprovalProvider>
          <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? ''}>
            {userIsLoggingIn ||
            (session == null && account != null && !error) ? (
              <Center h={'100%'}>
                <Loader />
              </Center>
            ) : (
              <SearchProvider>
                {shouldShowHeaderAndFooter && <TopMenu />}
                <Container
                  className={classes.root}
                  styles={{
                    root: {
                      padding: 0,
                      width: '100%',
                      maxWidth: '100%',
                      margin: 0,
                    },
                  }}
                >
                  <Pages />
                </Container>
                {shouldShowHeaderAndFooter && <Footer />}
              </SearchProvider>
            )}
          </APIProvider>
        </PendingApprovalProvider>
      </NotificationProvider>
    </ErrorBoundary>
  )
}

export default App
